import { User } from "@fairfleet/geotab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  MAX_ACTIVE_TO,
  UserForm,
  canManageUser,
  checkUserCreate,
  checkUserUpdate,
  isSameUser,
  mergeUserForSet,
  passwordForAdd,
  shouldApplySaveSelection,
  userFormToAuthFields,
  userFormToPayload,
  userWriteErrorMessage,
} from "../usersPageHelpers";
import { useGeotabClient } from "@/components/GeotabClientContext";

interface Selection {
  creating: boolean;
  selectedUserId: string | null;
}

interface Params {
  form: UserForm;
  isCreating: boolean;
  selectedUser: User | null;
  me: User | null;
  assignableClearanceIds: Set<string>;
  /** Always holds the current selection, so a save that finishes late can tell if it is stale. */
  selectionRef: MutableRefObject<Selection>;
  setIsCreating: (creating: boolean) => void;
  setSelectedUserId: (id: string | null) => void;
  setFormError: (message: string | null) => void;
  setFormSuccess: (message: string | null) => void;
}

/** Create/update, archive and delete of a user. Every write re-reads the user and re-checks rights. */
export function useUserMutations({
  form,
  isCreating,
  selectedUser,
  me,
  assignableClearanceIds,
  selectionRef,
  setIsCreating,
  setSelectedUserId,
  setFormError,
  setFormSuccess,
}: Params) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { geotabClient, activeSessionId } = useGeotabClient();

  const fetchFreshUser = async (id: string): Promise<User> => {
    const fresh = (await geotabClient.get("User", { id }, 1))[0];
    if (!fresh) throw new Error(t("users.validationSelection"));
    return fresh;
  };

  const fetchManageableUser = async (id: string): Promise<User> => {
    const fresh = await fetchFreshUser(id);
    if (!canManageUser(fresh, assignableClearanceIds)) throw new Error(t("users.cannotManageUser"));
    return fresh;
  };

  const invalidateUsers = async () => {
    await queryClient.invalidateQueries({ queryKey: ["usersBySearch", activeSessionId] });
    await queryClient.invalidateQueries({ queryKey: ["user", activeSessionId] });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      setFormError(null);
      setFormSuccess(null);
      if (!form.name.trim()) throw new Error(t("users.validationEmail"));
      if (!form.firstName.trim()) throw new Error(t("users.validationFirstName"));
      if (form.securityGroups.length === 0) throw new Error(t("users.validationSecurity"));
      if (
        isCreating &&
        form.userAuthenticationType === "BasicAuthentication" &&
        !form.password.trim()
      ) {
        throw new Error(t("users.validationPassword"));
      }

      const payload = userFormToPayload(form);
      const securityGroupIds = form.securityGroups.map(group => group.id);
      const authFields = userFormToAuthFields(form);

      if (isCreating) {
        const denial = checkUserCreate(securityGroupIds, assignableClearanceIds);
        if (denial) throw new Error(t(`users.${denial}`));
        const password = passwordForAdd(form.password);
        const newId = await geotabClient.add("User", {
          ...payload,
          ...authFields,
          activeFrom: new Date(),
          activeTo: MAX_ACTIVE_TO,
          ...(password ? { password } : {}),
        });
        return typeof newId === "string" ? newId : "";
      }

      if (!selectedUser) throw new Error(t("users.validationSelection"));

      const fresh = await fetchFreshUser(selectedUser.id);
      const denial = checkUserUpdate(fresh, securityGroupIds, me, assignableClearanceIds);
      if (denial) throw new Error(t(`users.${denial}`));

      await geotabClient.set("User", mergeUserForSet(fresh, { ...payload, ...authFields }));
      return selectedUser.id;
    },
    onMutate: () => ({ ...selectionRef.current }),
    onSuccess: async (userId, _vars, started) => {
      setFormSuccess(t("users.saveSuccess"));
      if (started && shouldApplySaveSelection(started, selectionRef.current)) {
        setIsCreating(false);
        setSelectedUserId(userId || null);
      }
      await invalidateUsers();
    },
    onError: error => setFormError(userWriteErrorMessage(t, error)),
  });

  const archiveMutation = useMutation({
    mutationFn: async (archive: boolean) => {
      if (!selectedUser) throw new Error(t("users.validationSelection"));
      if (isSameUser(selectedUser, me)) throw new Error(t("users.selfProtected"));

      const fresh = await fetchManageableUser(selectedUser.id);
      await geotabClient.set(
        "User",
        mergeUserForSet(fresh, { activeTo: archive ? new Date() : MAX_ACTIVE_TO }),
      );
    },
    onSuccess: async () => {
      setFormSuccess(t("users.archiveSuccess"));
      setFormError(null);
      await invalidateUsers();
    },
    onError: error => setFormError(userWriteErrorMessage(t, error)),
  });

  const removeMutation = useMutation({
    mutationFn: async () => {
      if (!selectedUser) throw new Error(t("users.validationSelection"));
      if (isSameUser(selectedUser, me)) throw new Error(t("users.selfProtected"));
      await fetchManageableUser(selectedUser.id);
      await geotabClient.remove("User", { id: selectedUser.id });
    },
    onSuccess: async () => {
      setFormSuccess(t("users.deleteSuccess"));
      setFormError(null);
      setSelectedUserId(null);
      setIsCreating(false);
      await invalidateUsers();
    },
    onError: error => setFormError(userWriteErrorMessage(t, error)),
  });

  return { saveMutation, archiveMutation, removeMutation };
}
